import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { ArrowRightIcon, ClockIcon, HomeIcon } from '@heroicons/react/solid'
import { BullseyeIcon } from '@components/UI/Icons/bullseye'
import { PaperPlaneIcon } from '@components/UI/Icons/paperPlane'

export default function HomeSellers() {
  return (
    <>
      <NextSeo
        title="Home Repair Pricing for Home Sellers - Find Repairs That Matter"
        description="Maximize your home's value and your time by finding repairs to your home that matter. Decide what to fix before listing. Get started today."
        canonical="https://majordomo.com/repair-pricing/-home-sellers"
        openGraph={{
          url: 'https://majordomo.com/repair-pricing/-home-sellers',
          title:
            'Home Repair Pricing for Home Sellers - Find Repairs That Matter | Majordomo',
          description:
            "Maximize your home's value and your time by finding repairs to your home that matter. Decide what to fix before listing. Get started today.",
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/sellers-hero.png',
              width: 700,
              height: 680,
              alt: 'A couple standing in front of a home with a sold sign'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <HomeIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Repair Pricing for Home Sellers
              </h1>
            </div>

            <p className="lg:mb-8 md:mb-4 mb-8 lg:text-2xl md:text-lg text-xl font-light">
              <span className="font-black">Get clarity</span> on your home's
              condition,
              <br />
              Focus on the{' '}
              <span className="font-black">repairs that matter</span>,<br />&{' '}
              <span className="font-black">Close the sale</span> faster
            </p>

            <p className="w-full max-w-lg md:p-0 px-8">
              Start with a pre-inspection and follow it up with a Domoreport to
              decide what to fix. It's all about maximizing your home's value,
              your time – and closing the sale faster.
            </p>
          </div>

          <img
            src="/images/product-pages/sellers-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 mb-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 max-w-md mx-auto">
            Deciding what to fix before listing? The Domoreport helps you take
            action
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-12 md:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center lg:order-first order-last">
              <div className="flex relative">
                <PaperPlaneIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Minimize contractor visits
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Once you receive your pre-inspection, upload the PDF and
                    we'll create your Domoreport. A human reviews your
                    pre-inspection. Defects are researched to get repair details
                    and local cost estimates. We compile the Domoreport and have
                    it back to you within 24 hours.{' '}
                    <span className="font-black">
                      No contractor visits are required to get quotes
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <BullseyeIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Optimize your repairs
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Use the Domoreport's repair cost estimates to determine
                    which items to repair to give you the biggest return on
                    investment.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ClockIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Close the sale faster
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Inspections often uncover problems that can scare off
                    buyers. A pre-inspection and Domoreport uncovers issues you
                    can repair ahead of time so that the buyer's inspection
                    comes back clean.{' '}
                    <span className="font-black">
                      No credit or repair requests
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="flex relative self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8 ml-0">
                <h4 className="max-w-lg md:text-2xl text-lg font-black tracking-tight mb-2">
                  All for just $99
                  <span className="font-light text-base"> / per report</span>
                </h4>
              </div>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 lg:order-last order-first">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="xl:max-w-xl lg:max-w-md max-w-xs"
              />
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-36 pb-12 md:max-w-3xl max-w-md mx-auto">
          Buying another home? We can help with that too.{' '}
          <Link href="/repair-pricing/home-buyers">
            <a className="text-accentBlue hover:underline">Learn More</a>
          </Link>
        </h4>
      </main>

      <Footer />
    </>
  )
}
